"use client"

import { useRef } from "react"
import { useLocation } from "react-router-dom"
import { Box, Flex, Image, Text, Tooltip } from "@chakra-ui/react"
import { useSupplierRetailer } from "../wrappers/SupplierRetailerContext"
import { neutral, brand, type, radius, motion } from "../theme/tokens"

const RETAILER_LOGOS = {
  PNP: "/pnp.jpg",
  CHK: "/chk.jpg",
  MED: "/medirite.jpeg",
  PET: "/petshop.jpeg",
  DIS: "/dis.jpg",
  CLI: "/cli.jpg",
  MAK: "/mak.jpg",
}

/**
 * The channel rail: which retailer's data you are looking at.
 *
 * This is the highest-frequency control in the product, so it stays visible at
 * the top of every screen. It renders inline — the shell owns the sticky
 * positioning, so this no longer fixes itself to `left: 200px` and can't drift
 * out of alignment with the sidebar.
 *
 * Semantics are a tablist: arrow keys move between channels, so it is reachable
 * with one Tab stop instead of seven.
 */
const RetailerNavigation = () => {
  const { selectedRetailer, setSelectedRetailer, retailers, availableRetailers } = useSupplierRetailer()
  const railRef = useRef(null)
  // Data Status (Helena 2026-10-20): every retailer tab is open there, whatever the selected supplier's retailers — the
  // page is about the feeds, not the supplier, so she can walk PnP → Checkers → Dis-Chem → Clicks without hunting for a
  // supplier that has each one.
  const { pathname } = useLocation()
  const isMaster = ["1", "2"].includes(localStorage.getItem("userid"))
  const allOpen = isMaster && pathname.startsWith("/data-status") // an org admin keeps the organisation's retailers (Linda: no PnP / Makro / Petshop)
  const isOpen = (value) => (allOpen && value !== "MAK") || Boolean(availableRetailers[value]) // Makro stays grey: no data from them (Helena)

  const enabled = retailers.filter((r) => isOpen(r.value))

  const handleKeyDown = (event) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1 }
    const step = keys[event.key]
    if (!step || enabled.length === 0) return
    event.preventDefault()
    const current = enabled.findIndex((r) => r.value === selectedRetailer)
    const next = enabled[(current + step + enabled.length) % enabled.length]
    setSelectedRetailer(next.value)
    railRef.current?.querySelector(`[data-retailer="${next.value}"]`)?.focus()
  }

  return (
    <Flex
      ref={railRef}
      role="tablist"
      aria-label="Retailer"
      align="center"
      gap={1}
      onKeyDown={handleKeyDown}
      overflowX="auto"
      overflowY="hidden"
      h="100%"
      css={{
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {retailers.map((retailer) => {
        const isAvailable = isOpen(retailer.value)
        const isActive = selectedRetailer === retailer.value

        const chip = (
          <Flex
            as="button"
            type="button"
            key={retailer.value}
            role="tab"
            data-retailer={retailer.value}
            aria-selected={isActive}
            aria-disabled={!isAvailable}
            tabIndex={isActive ? 0 : -1}
            disabled={!isAvailable}
            onClick={() => isAvailable && setSelectedRetailer(retailer.value)}
            align="center"
            gap={2}
            flexShrink={0}
            pl="6px"
            pr={3}
            py="5px"
            borderRadius={radius.sm}
            border="1px solid"
            borderColor={isActive ? brand[200] : "transparent"}
            bg={isActive ? brand[50] : "transparent"}
            color={isActive ? brand[600] : isAvailable ? neutral[600] : neutral[400]}
            cursor={isAvailable ? "pointer" : "not-allowed"}
            opacity={isAvailable ? 1 : 0.45}
            transition={`background-color ${motion.fast}, border-color ${motion.fast}, color ${motion.fast}`}
            _hover={isAvailable && !isActive ? { bg: neutral[100], color: neutral[900] } : undefined}
            _focusVisible={{ outline: `2px solid ${brand[500]}`, outlineOffset: "1px" }}
          >
            <Image
              src={RETAILER_LOGOS[retailer.value]}
              alt=""
              boxSize="22px"
              borderRadius={radius.sm}
              objectFit="contain"
              bg={neutral[0]}
              // Unavailable channels desaturate rather than disappear: you can
              // still see which retailers exist, just not open them.
              filter={isAvailable ? "none" : "grayscale(1)"}
              fallbackSrc="/blank.jpg"
            />
            <Text
              fontSize={type.size.small}
              fontWeight={isActive ? type.weight.semibold : type.weight.medium}
              whiteSpace="nowrap"
            >
              {retailer.text}
            </Text>
          </Flex>
        )

        if (isAvailable) return chip

        return (
          <Tooltip
            key={retailer.value}
            label={`No ${retailer.text} data for this supplier`}
            placement="bottom"
            openDelay={300}
          >
            <Box>{chip}</Box>
          </Tooltip>
        )
      })}
    </Flex>
  )
}

export default RetailerNavigation
